import ParkingDetailsSummary from 'components/receipt/ParkingDetailsSummary';
import ParkingTime from 'components/receipt/ParkingTime';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { View } from 'react-native';
import {
  BillingItem,
  ChargingSession,
  Maybe,
  ParkingSession,
  ParkZone,
  Receipt,
} from 'types/generatedSchemaTypes';
import { getCorrectBillingItem } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getLocationIconType } from 'utils/parkUtils';
import { getPaymentMethodProps } from 'utils/paymentUtils';

type SessionDetailsProps = {
  parkingSession?: ParkingSession;
  receiptData?: Receipt;
  chargingSession?: ChargingSession;
  parkZone: ParkZone;
  testID: string;
};

const SessionDetails: React.FC<SessionDetailsProps> = ({
  parkZone,
  parkingSession,
  receiptData,
  chargingSession,
  testID,
}) => {
  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);
  const { paymentState, sessionId } = parkingSession || {};

  const {
    name,
    locationType,
    parkingMethods,
    info,
    address,
    commercialZoneView,
  } = parkZone || {};

  const billingItem = getCorrectBillingItem(
    receiptData?.items as [BillingItem] | undefined
  )?.[0];

  const countryCode =
    parkingSession?.countryCode ||
    receiptData?.countryCode ||
    chargingSession?.countryCode ||
    'FI';
  const startDate =
    parkingSession?.startDate ||
    billingItem?.startTime ||
    chargingSession?.startTime;
  const endDate =
    parkingSession?.endDate ||
    billingItem?.endTime ||
    chargingSession?.endTime ||
    parkingSession?.predefinedEndDate;

  const isPaymentFailed =
    !!sessionId && (paymentState === 'Failed' || paymentState === 'Retrying');

  const session = parkingSession ?? chargingSession;
  const transactionId =
    parkingSession?.transactionId ||
    receiptData?.transactionId ||
    chargingSession?.transactionId;
  const totalAmount =
    parkingSession?.price?.totalAmount || chargingSession?.priceInCents;
  const paymentMethodProps = getPaymentMethodProps({
    session,
    receiptData,
    chargingPaymentType: chargingSession?.paymentType,
    transactionId,
    totalAmount,
  });

  const getTime = (
    primaryDate: Maybe<Date> | string | undefined,
    secondaryDate: Maybe<Date> | undefined
  ) => {
    if (primaryDate) {
      return new Date(primaryDate);
    } else if (secondaryDate) {
      return new Date(secondaryDate);
    }
    return null;
  };
  const startTime = getTime(startDate, undefined);
  const endTime = getTime(endDate, undefined);
  const markerType = getLocationIconType(
    locationType,
    showCameraRecognition && parkingMethods?.anpr,
    !isNil(chargingSession)
  );

  return (
    <View testID={testID}>
      <ParkingTime
        startTime={startTime}
        endTime={endTime}
        countryCode={countryCode}
      />
      <ParkingDetailsSummary
        startTime={startTime}
        endTime={endTime}
        locationType={locationType}
        name={commercialZoneView?.name || name}
        markerType={markerType}
        doorCode={info?.doorCode}
        coordinates={address?.marker?.coordinates}
        poolingInfo={parkingSession?.poolingInfo}
        failedPayment={isPaymentFailed}
        cardId={paymentMethodProps.cardId}
        transactionId={paymentMethodProps.transactionId}
        corporateAccountUid={paymentMethodProps.corporateAccountUid}
        totalAmount={paymentMethodProps.totalAmount}
        paymentType={paymentMethodProps.paymentType}
      />
    </View>
  );
};

export default SessionDetails;
